import { useCallback, useEffect, useRef } from 'react';
import { useRive, Layout, Fit, EventType } from '@rive-app/react-canvas';
import type { GameQuestion, TeamId } from '@/utils/quizQueue';
import shuffleRive from '@/assets/rive/shuffle.riv?url';

import styles from './ShuffleOverlay.module.css';

const STATE_MACHINE_NAME = 'SM';
const FALLBACK_TIMEOUT_MS = 5000;
const MAX_RIVE_CARDS = 9;

const TEAM_RIVE_COLORS: Record<TeamId, number> = {
  teletekst: 0xffca51d8,
  trending: 0xff00a6e2
};

type ShuffleOverlayProps = {
  quizQueue: GameQuestion[];
  onDone: () => void;
};

const ShuffleOverlay = ({ quizQueue, onDone }: ShuffleOverlayProps) => {
  const didFinish = useRef(false);

  const finish = useCallback(() => {
    if (didFinish.current) return;
    didFinish.current = true;
    onDone();
  }, [onDone]);

  const { rive, RiveComponent } = useRive({
    src: shuffleRive,
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
    autoBind: true,
    layout: new Layout({ fit: Fit.Cover })
  });

  useEffect(() => {
    if (!rive) return;

    const bindData = () => {
      setTimeout(() => {
        try {
          const vmInstance = (rive as any).viewModelInstance;
          if (!vmInstance) return;

          const root = vmInstance.viewModel('VMFlow') || vmInstance;

          quizQueue.slice(0, MAX_RIVE_CARDS).forEach((question, i) => {
            const num = String(i + 1).padStart(2, '0');
            const card = root.viewModel(`Card_${num}_Main`) || root.viewModel(`Card_${num}`);

            if (!card) return;

            const colorProp = card.color('colorProperty');
            const stringProp = card.string('stringProperty');

            if (colorProp) colorProp.value = TEAM_RIVE_COLORS[question.team];
            if (stringProp) stringProp.value = question.text;
          });

          const startProperty = root.boolean?.('Start') || root.trigger?.('Start');
          if (startProperty) {
            startProperty.value = true;
            return;
          }

          const inputs = rive.stateMachineInputs(STATE_MACHINE_NAME);
          const startInput = inputs.find(i => i.name === 'Start');
          if (startInput) startInput.value = true;
        } catch (error) {
          console.error('[ShuffleOverlay] Binding failed:', error);
        }
      }, 300);
    };

    rive.on(EventType.Load, bindData);
    if ((rive as any).viewModelInstance) bindData();

    return () => {
      rive.off(EventType.Load, bindData);
    };
  }, [rive, quizQueue]);

  useEffect(() => {
    if (!rive) return;

    const onStateChange = (event: any) => {
      const states = event.data as string[];
      const isFinished = states.some(s =>
        ['done', 'exit', 'end', 'finish', 'complete'].some(key => s.toLowerCase().includes(key))
      );
      if (isFinished) finish();
    };

    rive.on(EventType.StateChange, onStateChange);

    const fallback = window.setTimeout(() => {
      console.warn('[ShuffleOverlay] Fallback finish triggered. SM probably did not start.');
      finish();
    }, FALLBACK_TIMEOUT_MS);

    return () => {
      rive.off(EventType.StateChange, onStateChange);
      window.clearTimeout(fallback);
    };
  }, [rive, finish]);

  return (
    <div className={styles.container} aria-label="Shuffle animation">
      <RiveComponent className={styles.canvas} />
    </div>
  );
};

export default ShuffleOverlay;

